const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -60px 0px',
    threshold: 0.08,
  });
  animationTriggerElements.forEach((element) => observer.observe(element));
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!elementIsVisible) return;

        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      }),
      { passive: true }
    );
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    return 100;
  }

  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

// Smooth parallax for hero/banner images
function initializeParallax() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const parallaxEls = Array.from(document.querySelectorAll('.banner__media, .section-image-banner .media'));
  if (parallaxEls.length === 0) return;

  window.addEventListener('scroll', throttle(() => {
    parallaxEls.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;
      const speed = 0.25;
      const offset = (rect.top + rect.height / 2 - window.innerHeight / 2) * speed;
      el.style.transform = `translateY(${offset}px)`;
    });
  }), { passive: true });
}

// Staggered cascade for grid items
function initializeCascadeAnimations() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const grids = Array.from(document.querySelectorAll('.product-grid, .collection-list, .blog-articles'));

  grids.forEach((grid) => {
    const items = Array.from(grid.querySelectorAll('.grid__item, .article'));
    items.forEach((item, i) => {
      if (!item.classList.contains(SCROLL_ANIMATION_TRIGGER_CLASSNAME)) {
        item.classList.add(SCROLL_ANIMATION_TRIGGER_CLASSNAME, 'animate--slide-in', SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        item.setAttribute('data-cascade', '');
        item.style.setProperty('--animation-order', i);
      }
    });
  });
}

// Smooth scroll progress indicator
function initializeScrollProgress() {
  const bar = document.createElement('div');
  bar.id = 'ns-scroll-progress';
  bar.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    height: 2px;
    width: 0%;
    background: var(--color-button, #1a1a1a);
    z-index: 9999;
    transition: width 0.1s linear;
    pointer-events: none;
  `;
  document.body.appendChild(bar);

  window.addEventListener('scroll', throttle(() => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
    bar.style.width = `${progress}%`;
  }), { passive: true });
}

// Smooth page transitions on internal link clicks
function initializeSmoothTransitions() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const style = document.createElement('style');
  style.textContent = `
    html { scroll-behavior: smooth; }

    body {
      opacity: 1;
      transition: opacity 0.25s ease;
    }

    body.ns-page-leaving {
      opacity: 0;
    }

    .scroll-trigger.animate--fade-in {
      transition: opacity 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94),
                  transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important;
    }

    .scroll-trigger.animate--slide-in {
      transition: opacity 0.65s cubic-bezier(0.25, 0.46, 0.45, 0.94),
                  transform 0.65s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important;
    }
  `;
  document.head.appendChild(style);

  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href]');
    if (!link) return;
    const href = link.getAttribute('href');
    if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:') || link.target === '_blank') return;
    try {
      const url = new URL(href, window.location.origin);
      if (url.origin !== window.location.origin) return;
    } catch (_) { return; }

    e.preventDefault();
    document.body.classList.add('ns-page-leaving');
    setTimeout(() => { window.location.href = href; }, 250);
  });

  window.addEventListener('pageshow', () => {
    document.body.classList.remove('ns-page-leaving');
    document.body.style.opacity = '1';
  });
}

// Section-level reveal with subtle upward drift
function initializeSectionReveal() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const style = document.createElement('style');
  style.textContent = `
    .shopify-section:not(.shopify-section-header):not(.shopify-section-footer) {
      transition: opacity 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94),
                  transform 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    }
    .shopify-section.ns-offscreen {
      opacity: 0;
      transform: translateY(28px);
    }
    .shopify-section.ns-visible {
      opacity: 1;
      transform: translateY(0);
    }
  `;
  document.head.appendChild(style);

  const sections = Array.from(document.querySelectorAll(
    '.shopify-section:not(.shopify-section-header):not(.shopify-section-footer)'
  ));

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.remove('ns-offscreen');
        entry.target.classList.add('ns-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.06, rootMargin: '0px 0px -40px 0px' });

  sections.forEach((section) => {
    const rect = section.getBoundingClientRect();
    if (rect.top > 80) {
      section.classList.add('ns-offscreen');
    } else {
      section.classList.add('ns-visible');
    }
    observer.observe(section);
  });
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
  initializeCascadeAnimations();
  initializeParallax();
  initializeScrollProgress();
  initializeSmoothTransitions();
  initializeSectionReveal();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}
